import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_PATHS } from '../../../utils/constants';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Event as EventIcon
} from '@mui/icons-material';
// Status chip component
const StatusChip = ({ status }) => {
  let color = 'default';
  
  switch (status) {
    case 'CREATED':
      color = 'info';
      break;
    case 'IN_PROGRESS':
      color = 'warning';
      break;
    case 'COMPLETED':
      color = 'success';
      break;
    case 'CANCELLED':
      color = 'error';
      break;
    case 'PENDING':
      color = 'default';
      break;
    default:
      color = 'default';
  }
  
  return <Chip label={status} color={color} size="small" />;
};

// Ticket card component
const TicketCard = ({ ticket }) => {
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1">
            {ticket.stepName}
          </Typography>
          <StatusChip status={ticket.status} />
        </Box>
        
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Ticket Number: {ticket.ticketNumber}
        </Typography>
        
        <Typography variant="body2" color="textSecondary">
          Service: {ticket.serviceName}
        </Typography>
        
        <Divider sx={{ my: 1 }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="textSecondary">
            Created: {new Date(ticket.createDateTime).toLocaleDateString()}
          </Typography>
          {ticket.completedDateTime && (
            <Typography variant="body2" color="textSecondary">
              Completed: {new Date(ticket.completedDateTime).toLocaleDateString()}
            </Typography>
          )}
        </Box>
        
        {ticket.assignedTo && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="textSecondary">
              Assigned to: {ticket.assignedToName}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const WorkflowInstanceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  
  // Get the formatted request ID from location state if available
  const formattedRequestId = location.state?.requestId;

  useEffect(() => {
    const fetchInstanceData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await axios.get(`${API_PATHS.V1}/workflow/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const instanceData = response.data;
        if (!instanceData) {
          console.error('Workflow instance not found');
          navigate('/admin/workflow/instance');
          return;
        }
        
        setInstance(instanceData);
      } catch (error) {
        console.error('Error fetching workflow instance data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInstanceData();
  }, [id, navigate]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleBack = () => {
    navigate('/admin/workflow/instance');
  };

  // Sort tickets by step number
  const sortedTickets = instance?.tickets.sort((a, b) => {
    const stepA = instance.tickets.findIndex(t => t.id === a.id);
    const stepB = instance.tickets.findIndex(t => t.id === b.id);
    return stepA - stepB;
  });

  // Calculate current active step for stepper
  const getActiveStep = () => {
    if (!instance) return 0;
    
    if (instance.status === 'COMPLETED') {
      return instance.tickets.length;
    }
    
    const completedCount = instance.tickets.filter(t => t.status === 'COMPLETED').length;
    return completedCount;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!instance) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Workflow instance not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Back to Instances
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="h5" component="h1">
              {instance.workflowName}
            </Typography>
            <Chip
              label={formattedRequestId || instance.formattedRequestId || 
                (instance.organizationRequestPrefix && instance.requestNumber ? 
                  `${instance.organizationRequestPrefix}-R-${instance.requestNumber}` : 
                  `ID: ${id.substring(0, 8)}`)}
              color="primary"
              size="medium"
              sx={{ ml: 2, fontWeight: 'bold' }}
            />
          </Box>
          <Typography variant="subtitle1" color="textSecondary">
            Created: {new Date(instance.createdAt || instance.createDateTime).toLocaleString()}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1">
              Status: <StatusChip status={instance.status} />
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Customer: {instance.customerName}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">
              Created: {new Date(instance.createDateTime).toLocaleString()}
            </Typography>
            {instance.completedDateTime && (
              <Typography variant="body2" color="textSecondary">
                Completed: {new Date(instance.completedDateTime).toLocaleString()}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stepper activeStep={getActiveStep()} alternativeLabel sx={{ mb: 3 }}>
          {sortedTickets.map((ticket) => (
            <Step key={ticket.id} completed={ticket.status === 'COMPLETED'}>
              <StepLabel>{ticket.stepName}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Tickets" />
          <Tab label="Common Data" />
          <Tab label="Timeline" />
        </Tabs>
        
        <Box sx={{ p: 2 }}>
          {tabValue === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Tickets
              </Typography>
              <Grid container spacing={2}>
                {sortedTickets.map((ticket) => (
                  <Grid item key={ticket.id} xs={12} sm={6} md={4}>
                    <TicketCard ticket={ticket} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          
          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Common Information
              </Typography>
              <Paper elevation={1} sx={{ p: 2 }}>
                {instance.commonData && Object.entries(instance.commonData).map(([key, value]) => (
                  <Box key={key} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" component="span" sx={{ mr: 1 }}>
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                    </Typography>
                    <Typography variant="body2" component="span">
                      {typeof value === 'string' && value.includes('T') && new Date(value).toString() !== 'Invalid Date'
                        ? new Date(value).toLocaleString()
                        : value}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}
          
          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Timeline
              </Typography>
              <List>
                {instance.statusHistory.map((event, index) => (
                  <ListItem key={index} sx={{ mb: 2 }}>
                    <ListItemIcon>
                      <Avatar sx={{
                        bgcolor: 
                          event.status === 'COMPLETED' ? 'success.main' :
                          event.status === 'CANCELLED' ? 'error.main' :
                          event.status === 'IN_PROGRESS' ? 'warning.main' : 'info.main'
                      }}>
                        {event.status === 'COMPLETED' ? <CheckIcon /> : 
                         event.status === 'CANCELLED' ? <CloseIcon /> : 
                         <EventIcon />}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle2">{event.status}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(event.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          Updated by: {event.updatedBy}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default WorkflowInstanceDetail;
